import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { TextField, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";

function MyMUIDataGridTable() {
  const [rowData, setRowData] = useState([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();

    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://backend.form-seef.com/api/get-forms",
          { cancelToken: source.token }
        );

        if (isMounted && response.data && response.data.data) {
          const tableData = response.data.data.map((item) => ({
            id: item.id,
            // applyAs: item.academic.candidate,
            universityRegNo: item.academic.roll_number,
            yearOfAdmission: item.academic.year_id,
            university: item.academic.university_id,
            cgpaPercentage: item.academic.percentage_id,
            discipline: item.academic.discipline_id,
            studentName: `${item.first_name} ${item.last_name}`,
            fatherGuardianName: item.father_name,
            studentCNIC: item.cnic,
            cellPhoneNo: item.mobile,
            domicile: item.domicile_id,
            fatherGuardianOccupation: item.income ? item.income[0].profession : "",
            totalIncomePerAnnum: item.total_income_per_annum,
            formsubmittedon : item.academic.created_at,
          }));
          setRowData(tableData);
        }
      } catch (error) {
        console.log(error);
        // Handle error here (e.g., show error message to the user)
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      source.cancel("Request canceled");
    };
  }, []);

  const filteredRowData = useMemo(() => {
    if (!searchText) {
      return rowData;
    }
    const lowerCaseSearchText = searchText.toLowerCase();
    return rowData.filter((row) =>
      Object.values(row).some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(lowerCaseSearchText)
      )
    );
  }, [rowData, searchText]);

  const handleEdit = (id) => {
    const rowToEdit = rowData.find((row) => row.id === id);
    if (rowToEdit) {
      Swal.fire({
        title: "Edit Row",
        text: "Are you sure you want to edit this row?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Edit",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .put(`https://backend.form-seef.com/api/update-form/${id}`, rowToEdit)
            .then((res) => {
              console.log("Data updated successfully:", res.data);
              const updatedRowData = rowData.map((row) =>
                row.id === id ? rowToEdit : row
              );
              setRowData(updatedRowData);
              Swal.fire("Success", "Data updated successfully", "success");
            })
            .catch((err) => {
              console.log("Error updating data:", err);
              Swal.fire("Error", "Failed to update data", "error");
            });
        }
      });
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete Row",
      text: "Are you sure you want to delete this row?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`https://backend.form-seef.com/api/delete-single-form/${id}`)
          .then((res) => {
            console.log("Data deleted successfully:", res.data);
            const updatedRowData = rowData.filter((row) => row.id !== id);
            setRowData(updatedRowData);
            Swal.fire("Success", "Data deleted successfully", "success");
          })
          .catch((err) => {
            console.log("Error deleting data:", err);
            Swal.fire("Error", "Failed to delete data", "error");
          });
      }
    });
  };

  const columns = [
    { field: "id", headerName: "SEEF ID", width: 70 },
    { field: "studentName", headerName: "STUDENT NAME", width: 200 },
    { field: "universityRegNo", headerName: "UNIV. REG. NO.", width: 200 },
    {
      field: "fatherGuardianName",
      headerName: "FATHER / GUARDIAN NAME",
      width: 200,
    },
    { field: "studentCNIC", headerName: "STUDENT CNIC", width: 150 },
    { field: "university", headerName: "UNIVERSITY", width: 150 },
    { field: "discipline", headerName: "DISCIPLINE", width: 150 },
    { field: "yearOfAdmission", headerName: "YEAR OF ADMISSION", width: 70 },
    { field: "cgpaPercentage", headerName: "CGPA / PERCENTAGE", width: 200 },
    { field: "cellPhoneNo", headerName: "CELL PHONE NO", width: 200 },
    { field: "domicile", headerName: "DOMICILE", width: 150 },
    {
      field: "fatherGuardianOccupation",
      headerName: "FATHER / GUARDIAN OCCUPATION",
      width: 150,
    },{
      field: "formsubmittedon",
      headerName: "FORM SUBMIT DATE",
      width: 150,
    },
    {
      field: "totalIncomePerAnnum",
      headerName: "TOTAL INCOME PER ANNUM",
      width: 150,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            aria-label="edit"
            onClick={() => handleEdit(params.row.id)}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            aria-label="delete"
            onClick={() => handleDelete(params.row.id)}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="" style={{ width: "100%" }}>
        <div className="p-3">
          <TextField
            type="text"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            variant="outlined"
            className="bg-white"
          />
        </div>
        <div className="bg-white h-96 p-3">
          <DataGrid
            rows={filteredRowData}
            columns={columns}
            components={{
              Toolbar: GridToolbar,
            }}
            paginationpageSize={10}
            rowsPerPageOptions={[10, 20, 50, rowData.length]}
            disableSelectionOnClick
          />
          
        </div>
      </div>
    </>
  );
}

export default MyMUIDataGridTable;
